/**
 * Route tree.
 *
 * `/`               → redirect to /app/dashboard
 * `/login`          → public
 * `/unauthorized`   → public (intended to be reached only via redirect)
 * `/app/*`          → wrapped in RequireAuth + AppLayout
 *   `/app/dashboard`  → role-specific dashboard
 */

import { createBrowserRouter, Navigate } from 'react-router'
import { RequireAuth }       from '@/components/auth/RequireAuth'
import { AppLayout }         from '@/components/layout/AppLayout'
import { TenantBrandShell }  from '@/lib/brand/TenantBrandShell'
import { LandingPage }       from '@/pages/landing/LandingPage'
import { LoginPage }         from '@/pages/auth/LoginPage'
import { UnauthorizedPage }  from '@/pages/auth/UnauthorizedPage'
import { DashboardRouter }   from '@/pages/dashboard/DashboardRouter'
import { TaskDetailPage }    from '@/pages/tasks/TaskDetailPage'
import { CreateTaskPage }    from '@/pages/tasks/CreateTaskPage'
import { BulkCreateTaskPage } from '@/pages/tasks/BulkCreateTaskPage'
import { TeamPage }          from '@/pages/admin/TeamPage'
import { RoomReadinessBoard } from '@/pages/board/RoomReadinessBoard'
import { RoomDetailPage }     from '@/pages/board/RoomDetailPage'
import { MCPCorrectionsPage } from '@/pages/admin/MCPCorrectionsPage'
import { AnomaliesPage }     from '@/pages/admin/AnomaliesPage'
import { FeeRequestsPage }   from '@/pages/admin/FeeRequestsPage'
import { OrgSettingsPage }   from '@/pages/admin/OrgSettingsPage'
import { ReportsPage }       from '@/pages/admin/ReportsPage'
import { BidToolPage }       from '@/pages/admin/BidToolPage'
import { AbandonedBelongingsQueuePage } from '@/pages/admin/AbandonedBelongingsQueuePage'
import { LeadsInboxPage }    from '@/pages/admin/LeadsInboxPage'
import { RequireRole }       from '@/components/auth/RequireRole'
import { PATHS }             from './paths'

export const router = createBrowserRouter([
  { path: PATHS.root,         element: <LandingPage /> },
  { path: PATHS.login,        element: <LoginPage /> },
  { path: PATHS.unauthorized, element: <UnauthorizedPage /> },
  {
    path: PATHS.app,
    element: (
      <RequireAuth>
        <TenantBrandShell>
          <AppLayout />
        </TenantBrandShell>
      </RequireAuth>
    ),
    children: [
      { index: true,           element: <Navigate to={PATHS.dashboard} replace /> },
      { path: 'dashboard',     element: <DashboardRouter /> },
      {
        path: 'tasks/new',
        element: (
          <RequireRole allowedRoles={['supervisor', 'contractor_admin', 'super_admin']}>
            <CreateTaskPage />
          </RequireRole>
        ),
      },
      {
        path: 'tasks/bulk-new',
        element: (
          <RequireRole allowedRoles={['supervisor', 'contractor_admin', 'super_admin']}>
            <BulkCreateTaskPage />
          </RequireRole>
        ),
      },
      { path: 'tasks/:taskId', element: <TaskDetailPage /> },
      {
        path: 'board',
        element: (
          <RequireRole allowedRoles={['supervisor', 'super_admin', 'contractor_admin', 'client_view', 'maintenance']}>
            <RoomReadinessBoard />
          </RequireRole>
        ),
      },
      {
        path: 'rooms/:roomId',
        element: (
          <RequireRole allowedRoles={['supervisor', 'super_admin', 'contractor_admin', 'client_view', 'maintenance']}>
            <RoomDetailPage />
          </RequireRole>
        ),
      },
      {
        path: 'corrections',
        element: (
          <RequireRole allowedRoles={['super_admin', 'contractor_admin']}>
            <MCPCorrectionsPage />
          </RequireRole>
        ),
      },
      {
        path: 'anomalies',
        element: (
          <RequireRole allowedRoles={['super_admin', 'contractor_admin', 'supervisor']}>
            <AnomaliesPage />
          </RequireRole>
        ),
      },
      {
        path: 'team',
        element: (
          <RequireRole allowedRoles={['super_admin', 'contractor_admin', 'supervisor']}>
            <TeamPage />
          </RequireRole>
        ),
      },
      {
        path: 'fee-requests',
        element: (
          <RequireRole allowedRoles={['super_admin', 'contractor_admin', 'owner_approver']}>
            <FeeRequestsPage />
          </RequireRole>
        ),
      },
      {
        path: 'settings',
        element: (
          <RequireRole allowedRoles={['super_admin', 'contractor_admin']}>
            <OrgSettingsPage />
          </RequireRole>
        ),
      },
      {
        path: 'reports',
        element: (
          <RequireRole allowedRoles={['super_admin', 'contractor_admin']}>
            <ReportsPage />
          </RequireRole>
        ),
      },
      {
        path: 'bid-tool',
        element: (
          <RequireRole allowedRoles={['super_admin', 'contractor_admin']}>
            <BidToolPage />
          </RequireRole>
        ),
      },
      {
        path: 'abandoned-belongings',
        element: (
          <RequireRole allowedRoles={['super_admin', 'contractor_admin', 'supervisor']}>
            <AbandonedBelongingsQueuePage />
          </RequireRole>
        ),
      },
      {
        path: 'leads',
        element: (
          <RequireRole allowedRoles={['super_admin']}>
            <LeadsInboxPage />
          </RequireRole>
        ),
      },
    ],
  },
  { path: '*', element: <Navigate to={PATHS.dashboard} replace /> },
])
